import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';

// Utils
import HocUtil from 'Utils/hoc-util';
import { getSectionId } from 'Utils/content/flipboard-urls';
import FlapUtil from 'Utils/content/flap-util';
import isIntegerMatch from 'Utils/is-integer-match';
import { USAGE_EVENT_NAMES } from 'Utils/analytics/usage';
import SectionUtil from 'Utils/content/section-util';

// Actions
import {
  followSections,
  unfollowSection,
} from '../redux/actions/profile-actions';
import { usageTrackTapFollowSectionAuthor } from 'Webapp/shared/app/redux/actions/usage-actions';
import { getSection } from 'Webapp/shared/app/redux/actions/section-actions';
// Components
import { SocialActionType } from 'Webapp/enums';

import withNavFrom from '../hocs/withNavFrom';
import withSocialActionGate from '../hocs/withSocialActionGate';

import connector from 'Utils/connector';
import connectAuthentication from 'Webapp/shared/app/connectors/connectAuthentication';
import connectUsageSetNavFrom from 'Webapp/shared/app/connectors/connectUsageSetNavFrom';

function withFollowAuthor(Component) {
  class WrappedComponent extends React.Component {
    constructor(props) {
      super(props);

      this.authorSectionId = this.authorSectionId.bind(this);
      this.authorSection = this.authorSection.bind(this);
      this.isFollowingAuthor = this.isFollowingAuthor.bind(this);
      this.handleFollowAuthor = this.handleFollowAuthor.bind(this);
      this.handleUnfollowAuthor = this.handleUnfollowAuthor.bind(this);
    }

    authorSectionId() {
      const { section, coverItem } = this.props;
      const author = SectionUtil.getAuthor(section, coverItem);
      if (author.remoteid) {
        return author.remoteid;
      }
      const username = author?.authorUsername;
      return (
        username &&
        getSectionId({
          username,
        })
      );
    }

    authorSection() {
      const authorSectionId = this.authorSectionId();

      const sectionEntry = FlapUtil.getSectionByRemoteId(
        authorSectionId,
        this.props.sections,
      );

      return sectionEntry;
    }

    isFollowingAuthor() {
      const { followingIds } = this.props;
      const section = this.authorSection();
      const remoteid = section && section.remoteid;
      // NOTE that SOMETIMES, a profile section has a remoteid in the format:
      // `flipboard/user%2F11111111` but is followed as `auth/flipboard/user%2F11111111`
      return followingIds.some((id) => id.includes(remoteid));
    }

    handleFollowAuthor() {
      const {
        section,
        navFrom,
        showSocialActionGate,
        usageSetNavFrom,
        usageTrackTapFollowSectionAuthor,
      } = this.props;
      if (this.props.isAuthenticated) {
        const authorSection = this.authorSection();
        usageSetNavFrom(USAGE_EVENT_NAMES.TAP_SECTION_SUBSCRIBE, navFrom);
        usageTrackTapFollowSectionAuthor(section, authorSection, navFrom);
        this.props.followSections(authorSection, navFrom, section);
      } else {
        showSocialActionGate(SocialActionType.FOLLOW);
      }
    }

    handleUnfollowAuthor() {
      const {
        section,
        navFrom,
        usageSetNavFrom,
        usageTrackTapFollowSectionAuthor,
      } = this.props;
      const authorSection = this.authorSection();
      usageSetNavFrom(USAGE_EVENT_NAMES.TAP_SECTION_SUBSCRIBE, navFrom);
      usageTrackTapFollowSectionAuthor(section, authorSection);
      this.props.unfollowSection(authorSection, navFrom, section);
    }

    render() {
      const authorSection = this.authorSection();
      const currentUserIsAuthor = isIntegerMatch(
        authorSection && authorSection.userid,
        this.props.uid,
      );

      return (
        <Component
          {...this.props}
          authorSectionId={this.authorSectionId()}
          authorSection={authorSection}
          isFollowingAuthor={this.isFollowingAuthor()}
          followSectionAuthor={
            currentUserIsAuthor ? null : this.handleFollowAuthor
          }
          unfollowSectionAuthor={
            currentUserIsAuthor ? null : this.handleUnfollowAuthor
          }
        />
      );
    }
  }

  // Proxy the wrapped component displayName
  WrappedComponent.displayName = HocUtil.displayName(Component);

  WrappedComponent.propTypes = {
    uid: PropTypes.number.isRequired,
    section: PropTypes.object.isRequired,
    coverItem: PropTypes.object,
    isAuthenticated: PropTypes.bool.isRequired,
    followingIds: PropTypes.array.isRequired,
    followSections: PropTypes.func.isRequired,
    unfollowSection: PropTypes.func.isRequired,
    navFrom: PropTypes.string,
    sections: PropTypes.array.isRequired,
    authorSectionId: PropTypes.string,
    authorSection: PropTypes.object,
    showSocialActionGate: PropTypes.func.isRequired,
    usageSetNavFrom: PropTypes.func.isRequired,
    usageTrackTapFollowSectionAuthor: PropTypes.func.isRequired,
    getSection: PropTypes.func,
  };

  WrappedComponent.defaultProps = {
    navFrom: null,
    authorSectionId: null,
    authorSection: null,
  };

  const mapStateToProps = ({ auth, profile, sections }) => {
    const { followingIds } = profile;
    return {
      uid: auth.uid,
      followingIds,
      sections: sections.entries,
    };
  };

  const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
      {
        getSection,
        followSections,
        unfollowSection,
        usageTrackTapFollowSectionAuthor,
      },
      dispatch,
    );

  return connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    connector(
      connectUsageSetNavFrom,
      connectAuthentication,
    )(withNavFrom(withSocialActionGate(WrappedComponent))),
  );
}

export default withFollowAuthor;
